import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { apiHandler } from '../../../api/apihandler';
import { NOTIFICATION_LIST, NOTIFICATION_READ, NOTIFICATION_DELETE } from '../../../api/constants';
import { apiSucceeded, formatRelativeTime, getAvatarColor, getInitials } from '../feed/feedUtils';
import PeopleYouMayKnowWidget from '../../../components/nemonto/PeopleYouMayKnowWidget';
import MyStatsWidget from '../../../components/nemonto/MyStatsWidget';
import PromoCard from '../../../components/nemonto/PromoCard';
import EmptyState from '../../../components/nemonto/EmptyState';
import './social-list-page.css';

const NotificationAvatar = ({ photo, name }) => {
  if (photo) return <img src={photo} alt="" className="nmsl-avatar" />;
  return (
    <div className="nmsl-avatar" style={{ background: getAvatarColor(name || 'User') }}>
      {getInitials(...(name || 'User').split(' '))}
    </div>
  );
};

// Notification `type` codes, as sent by the backend (see the legacy
// views/dashboard/app/notification.js generateURL for the original mapping
// this is ported from):
//   0 follow, 1 comment, 2 participate, 3 dance-corner post, 4 challenge post,
//   5 winner, 6 like, 7 tag, 8 hire, 10 event, 11 class, 12 registration form,
//   13 illness report, 14 accident report, 15 allergy action plan,
//   16 lunch plan waiver, 17 no-peanut policy, 18 emergency contact,
//   19 daily report published, 20 medication administered, 21 leave
//   application, 22 non-medication administered, 40 billing statement.
// `feed_type` disambiguates 1/6/7 between a recipe post (0) and a challenge
// post (anything else).
function resolveNotificationUrl(n, profileData) {
  const type = Number(n?.type);
  const feedType = Number(n?.feed_type);
  const valueId = n?.value_id ?? n?.valueId ?? n?.form_id ?? null;
  const friendSlug = n?.value_slug || n?.user?.slug;

  switch (type) {
    case 0: // Follow / follow request
      return '/dashboard/app/friend-request';
    case 1: // Comment
    case 6: // Like
    case 7: // Tag
      if (feedType === 0) return valueId ? `/pages/recipe/${valueId}` : '/pages/home';
      return valueId ? `/pages/challenge/${valueId}` : '/pages/home';
    case 2: // Participate
    case 3: // Dance corner post
    case 4: // Challenge post
    case 5: // Winner
      return valueId ? `/pages/challenge/${valueId}` : '/pages/home';
    case 8: // Hire
      return friendSlug ? `/dashboard/app/friend-profile/${friendSlug}` : '/pages/home';
    case 12: // Registration package submitted
      return '/dashboard/child-registration/list';
    case 15: // Allergy action plan
      return '/dashboard/allergy-action-plan/list';
    case 16: // Lunch plan waiver
      return '/dashboard/lunch-plan-waiver/list';
    case 17: // No peanut policy
      return '/dashboard/no-peanut-policy/list';
    case 18: // Emergency contact
      return '/dashboard/emergency-contact/list';
    case 19: // Daily report published - teachers see the room roster, parents see their own profile
      return profileData?.user_type === 4 ? '/dashboard/daily-reports' : '/dashboard/app/profile';
    case 21: // Leave application
      return '/dashboard/app/leave-application';
    case 40: // Billing statement
      return '/dashboard/app/profile';
    case 10: // Event
    case 11: // Class
    case 13: // Illness report
    case 14: // Accident report
    case 20: // Medication administered
    case 22: // Non-medication administered
      return '/pages/home';
    default:
      // Unknown type - best effort using whatever we have, rather than a
      // dead click that marks the notification read and goes nowhere.
      if (feedType === 0 && valueId) return `/pages/recipe/${valueId}`;
      return '/pages/home';
  }
}

const NotificationsPage = () => {
  const navigate = useNavigate();
  let profileData = useSelector((state) => state.setting.setting.user_data);
  profileData = profileData ? JSON.parse(profileData) : null;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  async function loadNotifications() {
    setLoading(true);
    try {
      const res = await apiHandler(NOTIFICATION_LIST, 'POST', { page: 1, limit: 50 });
      if (apiSucceeded(res)) setNotifications(res?.data?.data?.notifications?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadNotifications(); }, []);

  async function markRead(n) {
    if (n.is_read === 1) return;
    setNotifications((prev) => prev.map((item) => (item.id === n.id ? { ...item, is_read: 1 } : item)));
    // Tells the sidebar badge (a separate, page-load-fetched unread count
    // in the layout) to decrement immediately instead of staying stale
    // until the next full page load.
    window.dispatchEvent(new CustomEvent('notification-count-refresh', { detail: { delta: -1 } }));
    try {
      await apiHandler(NOTIFICATION_READ, 'POST', { notification_id: n.id });
    } catch (e) { console.error(e); }
  }

  // Read + navigate both happen on click, mirroring what a like/comment/
  // follow notification means: open whatever it's actually about (marking
  // it read is a side effect, not the point of the click). Read isn't
  // awaited - it fires in the background so navigation feels instant.
  function openNotification(n) {
    markRead(n);
    navigate(resolveNotificationUrl(n, profileData));
  }

  async function deleteNotification(n) {
    const prev = notifications;
    setNotifications((list) => list.filter((item) => item.id !== n.id));
    // Deleting an already-read notification doesn't change the unread
    // count, so only signal a decrement when the deleted item was unread.
    // If the delete turns out to have failed, the compensating dispatch
    // below undoes it - otherwise the sidebar badge would stay wrongly
    // decremented for a notification that's still there.
    const delta = n.is_read === 1 ? 0 : -1;
    window.dispatchEvent(new CustomEvent('notification-count-refresh', { detail: { delta } }));
    try {
      const res = await apiHandler(NOTIFICATION_DELETE, 'DELETE', { notification_id: n.id });
      if (!apiSucceeded(res)) {
        setNotifications(prev);
        window.dispatchEvent(new CustomEvent('notification-count-refresh', { detail: { delta: -delta } }));
      }
    } catch (e) {
      console.error(e);
      setNotifications(prev);
      window.dispatchEvent(new CustomEvent('notification-count-refresh', { detail: { delta: -delta } }));
    }
  }

  return (
    <div className="nmsl-layout">
      <div className="nmsl-main">
        <div className="nmsl-page-header">
          <div className="nmsl-header">
            <h3 className="nmsl-title">Notifications</h3>
            {notifications.length > 0 && <span className="nmsl-count-pill">{notifications.length}</span>}
          </div>
          <p className="nmsl-subtitle">Stay on top of likes, comments, and new followers.</p>
        </div>

        <div className="nmsl-main-body">
          {loading && <p className="nmsl-loading">Loading…</p>}
          {!loading && notifications.length === 0 && (
            <EmptyState icon="notifications" title="You're all caught up" sub="Likes, comments, and new followers will show up here." />
          )}

          {!loading && notifications.map((n) => (
            <div
              key={n.id}
              className={`nmsl-card nmsl-clickable ${n.is_read ? '' : 'nmsl-unread'}`}
              onClick={() => openNotification(n)}
            >
              <NotificationAvatar
                photo={n?.user?.user_setting?.photo}
                name={`${n?.user?.first_name || ''} ${n?.user?.last_name || ''}`}
              />
              <div className="nmsl-body">
                <div className={`nmsl-notif-text ${n.is_read ? '' : 'nmsl-unread-text'}`}>{n.description}</div>
                <div className="nmsl-meta">{formatRelativeTime(n.created_at)}</div>
              </div>
              <button
                type="button"
                className="nmsl-icon-btn"
                onClick={(e) => { e.stopPropagation(); deleteNotification(n); }}
                title="Delete"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      <aside className="nmsl-rail">
        <PromoCard
          label="For creators"
          title="Got a recipe worth sharing?"
          sub="Post it and let other cooks like, comment, and try it themselves."
          ctaText="Post a new recipe"
          to="/pages/post-submit"
        />
        <PeopleYouMayKnowWidget />
        <MyStatsWidget />
      </aside>
    </div>
  );
};

export default NotificationsPage;
